import type { SkillData } from '../spacetime/modules/SkillModule';
import type { AppearanceCatalogEntry, ItemCatalogEntry, MobCatalogEntry } from '../spacetime/types';
import { t } from '../i18n';
import { localizeItemName, localizeSkillDescription, localizeSkillName } from '../i18n/content';
import { drawActorSprite } from '../renderer/ActorSprite';
import { createMobIconElement } from '../renderer/mobVisuals/index';
import type { CodexListRow } from './codexTabRenderer';
import { isGreaterSkill } from './skillVariants';

export type CodexTab = 'skills' | 'items' | 'mobs' | 'appearances';

export interface CodexTabDefinition {
  key: CodexTab;
  label: string;
  emptyLabel: string;
  getRows: () => CodexListRow[];
}

export interface CodexDataSnapshot {
  playerSkills: SkillData[];
  items: ItemCatalogEntry[];
  mobs: MobCatalogEntry[];
  appearances: AppearanceCatalogEntry[];
  skillBarSkillIds: string[];
}

const FALLBACK_ICON = '📦';

function mobSpriteColor(entityType: string): string {
  return entityType === 'zombie' ? '#4ade80' : '#ef4444';
}

export function createMobSpriteIcon(entityType: string): HTMLElement {
  const imageIcon = createMobIconElement(entityType);
  if (imageIcon) {
    return imageIcon;
  }
  const size = 28;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  canvas.style.width = `${size}px`;
  canvas.style.height = `${size}px`;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return canvas;
  }
  drawActorSprite(ctx, {
    centerX: size / 2,
    centerY: size / 2,
    size: size * 0.9,
    rotation: 180,
    color: mobSpriteColor(entityType),
  });
  return canvas;
}

export function buildCodexTabDefinitions(snapshot: CodexDataSnapshot): CodexTabDefinition[] {
  const createTab = (
    name: CodexTab,
    label: string,
    emptyLabel: string,
    getRows: () => CodexListRow[]
  ): CodexTabDefinition => ({ key: name, label, emptyLabel, getRows });

  return [
    createTab('skills', t('character.tabs.skills'), t('character.noSkills'), () =>
      snapshot.playerSkills.map((skill) => {
        const inBar = snapshot.skillBarSkillIds.includes(skill.skillId);
        return {
          value: skill.skillId,
          icon: skill.emoji || FALLBACK_ICON,
          title: localizeSkillName(skill.skillId, skill.displayName),
          idText: skill.skillId,
          description: localizeSkillDescription(skill.skillId, skill.description),
          stats: [
            skill.damage > 0 ? t('character.statDmg', { value: skill.damage }) : '',
            skill.range > 0 ? t('character.statRange', { value: skill.range }) : '',
            skill.durationTicks > 0 ? t('character.statDuration', { value: skill.durationTicks }) : '',
            skill.cooldownTicks > 0 ? t('character.statCooldown', { value: skill.cooldownTicks }) : '',
            skill.staminaCost > 0 ? `Stamina ${skill.staminaCost}` : '',
          ].filter(Boolean).join(' · '),
          tags: skill.tags.includes('Support') ? ['Support'] : undefined,
          inBar,
          iconBadge: isGreaterSkill(skill.skillId) ? 'greater' : undefined,
        };
      })
    ),
    createTab('items', t('character.tabs.items'), t('character.noItems'), () =>
      snapshot.items.map((item) => ({
        value: item.itemId,
        icon: resolveItemEmoji(item.metadata),
        title: localizeItemName(item.itemId, item.metadata, item.displayName),
        idText: item.itemId,
        stats: `${item.itemId} | ${item.itemType} | Max Stack: ${item.maxStack}`,
      }))
    ),
    createTab('mobs', t('codex.tabs.mobs'), t('codex.emptyMobRegistry'), () =>
      snapshot.mobs.map((mob) => ({
        value: String(mob.mobTypeId),
        icon: createMobSpriteIcon(mob.entityType),
        title: mob.displayName,
        idText: String(mob.mobTypeId),
        stats: `Type: ${mob.entityType} | HP: ${mob.baseHp} | Mana: ${mob.baseMana} | Move: ${mob.moveDistance} | Speed: ${mob.speed}`,
      }))
    ),
    createTab('appearances', 'Appearances', 'No appearance definitions found.', () =>
      snapshot.appearances.map((appearance) => ({
        value: appearance.entityType,
        icon: createMobSpriteIcon(appearance.entityType),
        title: appearance.displayName,
        idText: appearance.entityType,
        stats: `Appearance: ${appearance.entityType}`,
      }))
    ),
  ];
}

function resolveItemEmoji(metadata: Record<string, unknown>): string {
  const emoji = metadata.emoji;
  return typeof emoji === 'string' && emoji.trim().length > 0 ? emoji : FALLBACK_ICON;
}
